import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, Image, Video, Music, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface HeartNotification {
  id: string;
  type: "heart";
  content_type: string;
  content_id: string;
  hearts_count: number;
  last_heart_at: string;
  is_read: boolean;
  content_title?: string;
}

interface SupportNotification {
  id: string;
  type: "support";
  support_message_id: string;
  subject: string;
  created_at: string;
  is_read: boolean;
}

type NotificationItem = HeartNotification | SupportNotification;

// Notification sound as base64 (short pleasant chime)
const NOTIFICATION_SOUND = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZqNVn5AAAAAAAAAAAAAAAAAAAAAAD/+9DEAAAH8ANn9AAAARUwbPr2EAADMQEBAQAAAQEBAQEBAQEBAQEBmZmZgYGBpf//hIQJmYGBgQEBAQEBAQEB//uEhIQEBAQEBAQEBP/7EMQWghNwAF/8AAAsIgC//4AAAAEBAQEAwoZmYMKGBm//9AgICAYMDA////8DA4ODgwODggICA//uQxAICEOAAA/8AAAoYAB/4AAAAEABAQEB/+BAQEAwMDICA////wMDAwMD//sQxBeCEJAAA/8AAAoAAA/4AAAAAAYHBw8PD/+BgcHBwc//+5DEIAIQoAAP/AAAJAAAH/gAAAAAAA4OHh4e//BwcHBwf/+xDEMoIQAAAP+AAADgAAD/gAAAAAAAAODh4eH/4ODg4OD/+5DEQwAPcABv/AAAAhQADP/AAAAAAAABw8PD/8HBwcHB//sQxFSCDoAAb/wAAAtAAG/8AAAAAAAA4eHh/+Dg4ODg";

export function NotificationBell() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prevUnreadCountRef = useRef<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isInitialLoadRef = useRef(true);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Play notification sound when new unread notifications arrive
  useEffect(() => {
    // Skip on initial load
    if (isInitialLoadRef.current) {
      prevUnreadCountRef.current = unreadCount;
      isInitialLoadRef.current = false;
      return;
    }

    // Play sound if unread count increased
    if (unreadCount > prevUnreadCountRef.current) {
      playNotificationSound();
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  const playNotificationSound = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND);
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Ignore autoplay restrictions
      });
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  };

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (!userId) return;

    fetchAllNotifications();

    // Subscribe to heart notifications
    const heartChannel = supabase
      .channel("heart-notifications")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "heart_notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          fetchAllNotifications();
        }
      )
      .subscribe();

    // Subscribe to support notifications
    const supportChannel = supabase
      .channel("support-notifications")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "support_notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          fetchAllNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(heartChannel);
      supabase.removeChannel(supportChannel);
    };
  }, [userId]);

  const checkUser = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    setUserId(session?.user?.id || null);
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const fetchContentTitle = async (
    contentType: string,
    contentId: string
  ): Promise<string | null> => {
    try {
      if (contentType === "video") {
        const { data } = await supabase
          .from("generated_videos")
          .select("prompt, name")
          .eq("id", contentId)
          .maybeSingle();
        return data?.name || data?.prompt || null;
      } else if (contentType === "image") {
        const { data } = await supabase
          .from("images")
          .select("prompt")
          .eq("id", contentId)
          .maybeSingle();
        return data?.prompt || null;
      } else if (contentType === "music") {
        const { data } = await supabase
          .from("generated_music")
          .select("title, prompt")
          .eq("id", contentId)
          .maybeSingle();
        return data?.title || data?.prompt || null;
      }
    } catch (error) {
      console.error("Error fetching content title:", error);
    }
    return null;
  };

  const fetchAllNotifications = async () => {
    if (!userId) return;

    // Fetch heart notifications
    const { data: heartData, error: heartError } = await supabase
      .from("heart_notifications")
      .select("*")
      .eq("user_id", userId)
      .order("last_heart_at", { ascending: false })
      .limit(15);

    if (heartError) {
      console.error("Error fetching heart notifications:", heartError);
    }

    // Fetch support notifications with message subject
    const { data: supportData, error: supportError } = await supabase
      .from("support_notifications")
      .select("id, support_message_id, is_read, created_at, support_messages(subject)")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(10);

    if (supportError) {
      console.error("Error fetching support notifications:", supportError);
    }

    // Process heart notifications with titles
    const heartNotifications: HeartNotification[] = await Promise.all(
      (heartData || []).map(async (n) => {
        const content_title = await fetchContentTitle(n.content_type, n.content_id);
        return {
          id: n.id,
          type: "heart" as const,
          content_type: n.content_type,
          content_id: n.content_id,
          hearts_count: n.hearts_count,
          last_heart_at: n.last_heart_at,
          is_read: n.is_read,
          content_title,
        };
      })
    );

    // Process support notifications
    const supportNotifications: SupportNotification[] = (supportData || []).map((n: any) => ({
      id: n.id,
      type: "support" as const,
      support_message_id: n.support_message_id,
      subject: n.support_messages?.subject || "Support request",
      created_at: n.created_at,
      is_read: n.is_read,
    }));

    // Merge and sort by date
    const allNotifications: NotificationItem[] = [
      ...heartNotifications,
      ...supportNotifications,
    ].sort((a, b) => {
      const dateA = a.type === "heart" ? new Date(a.last_heart_at) : new Date(a.created_at);
      const dateB = b.type === "heart" ? new Date(b.last_heart_at) : new Date(b.created_at);
      return dateB.getTime() - dateA.getTime();
    });

    setNotifications(allNotifications);
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    if (notification.type === "heart") {
      // Mark heart notification as read
      const { error } = await supabase
        .from("heart_notifications")
        .update({ is_read: true })
        .eq("id", notification.id);

      if (!error) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
      }

      // Navigate to content tab
      setIsOpen(false);
      navigate(`/?tab=${notification.content_type}`);
    } else {
      // Mark support notification as read
      const { error } = await supabase
        .from("support_notifications")
        .update({ is_read: true })
        .eq("id", notification.id);

      if (!error) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
      }

      // Navigate to Account & Support page
      setIsOpen(false);
      navigate("/account");
    }
  };

  const markAllAsRead = async () => {
    const unreadHeartIds = notifications
      .filter((n) => !n.is_read && n.type === "heart")
      .map((n) => n.id);
    const unreadSupportIds = notifications
      .filter((n) => !n.is_read && n.type === "support")
      .map((n) => n.id);

    if (unreadHeartIds.length > 0) {
      await supabase
        .from("heart_notifications")
        .update({ is_read: true })
        .in("id", unreadHeartIds);
    }

    if (unreadSupportIds.length > 0) {
      await supabase
        .from("support_notifications")
        .update({ is_read: true })
        .in("id", unreadSupportIds);
    }

    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case "image":
        return <Image className="h-4 w-4 text-muted-foreground" />;
      case "video":
        return <Video className="h-4 w-4 text-muted-foreground" />;
      case "music":
        return <Music className="h-4 w-4 text-muted-foreground" />;
      default:
        return null;
    }
  };

  const getContentLabel = (type: string) => {
    switch (type) {
      case "image":
        return t("common.image", "image");
      case "video":
        return t("common.video", "video");
      case "music":
        return t("common.music", "music");
      default:
        return type;
    }
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("common.justNow", "just now");
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  if (!userId) return null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm">
            {t("notifications.title", "Notifications")}
          </span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-auto py-1 px-2"
              onClick={markAllAsRead}
            >
              {t("notifications.markAllRead", "Mark all read")}
            </Button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="px-3 py-8 text-center text-muted-foreground text-sm">
              {t("notifications.empty", "No notifications yet")}
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={cn(
                  "flex items-start gap-3 px-3 py-3 cursor-pointer",
                  !notification.is_read && "bg-accent/50"
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {notification.type === "heart" ? (
                    <Heart className="h-5 w-5 text-red-500 fill-red-500" />
                  ) : (
                    <MessageSquare className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {notification.type === "heart" ? (
                    <>
                      <p className="text-sm">
                        <span className="font-medium">
                          {t("notifications.youGot", "You got")}{" "}
                          {notification.hearts_count}{" "}
                          {notification.hearts_count === 1
                            ? t("notifications.heart", "heart")
                            : t("notifications.hearts", "hearts")}
                        </span>{" "}
                        {t("notifications.on", "on")}{" "}
                        {notification.content_title
                          ? truncateText(notification.content_title)
                          : `${t("notifications.your", "your")} ${getContentLabel(notification.content_type)}`}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {getContentIcon(notification.content_type)}
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(notification.last_heart_at)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">
                        <span className="font-medium">
                          {t("notifications.supportReply", "Reply received")}
                        </span>{" "}
                        {t("notifications.for", "for")}{" "}
                        {truncateText(notification.subject)}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {!notification.is_read && (
                  <div className="flex-shrink-0">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  </div>
                )}
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
